import React from 'react';
import { Github, Linkedin, Briefcase, BrainCircuit, Users, Bot, LineChart, Code } from 'lucide-react';

// --- Reusable Components ---

// Avatar component with a glowing border effect
const AvatarComponent = ({ imageUrl, name }) => (
  <div className="relative inline-block">
    <img
      src={imageUrl}
      alt={name}
      className="h-28 w-28 rounded-full object-cover shadow-lg mx-auto relative z-10 border-2 border-slate-700"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'https://placehold.co/200x200/1E293B/94A3B8?text=Photo';
      }}
    />
    <div className="absolute inset-0 rounded-full bg-indigo-500 blur-lg animate-pulse-slow"></div>
  </div>
);

// Glassmorphism Card Component
const GlassCard = ({ children, className = '' }) => (
  <div
    className={`bg-slate-800/30 backdrop-blur-xl border border-slate-700 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 hover:border-indigo-500/50 hover:shadow-indigo-500/20 ${className}`}>
    {children}
  </div>
);

// Section component with a cleaner look
const Section = ({ title, children, className = '', id = '' }) => (
  <section id={id} className={`py-20 sm:py-28 relative overflow-hidden ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="text-center">
        <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-100 mb-4">{title}</h2>
        <div className="inline-block w-24 h-1 bg-indigo-500 rounded-full mb-12"></div>
      </div>
      {children}
    </div>
  </section>
);

// --- Page Components ---

// Header component with AI-centric title
const PageHeader = () => (
  <div className="text-center pt-24 pb-16">
    <h1 className="text-5xl md:text-7xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
      Our Neural Network
    </h1>
    <p className="mt-4 text-lg md:text-xl text-slate-400 max-w-3xl mx-auto">
      A synergy of human intellect and artificial intelligence, crafting the future.
    </p>
  </div>
);

// Founder's card with new styling
const FounderCard = ({ name, title, imageUrl, bio, githubUrl, linkedinUrl }) => (
  <GlassCard className="flex flex-col h-full text-center group">
    <div className="p-8 flex-grow flex flex-col items-center">
      <div className="flex-shrink-0 mb-6">
        <AvatarComponent imageUrl={imageUrl} name={name} />
      </div>
      <div className="flex-grow">
        <h3 className="text-2xl font-bold text-white">{name}</h3>
        <p className="text-indigo-400 font-semibold mt-1 transition-all duration-300 group-hover:text-cyan-400">
          {title}
        </p>
        <p className="mt-4 text-slate-400 leading-relaxed text-sm">{bio}</p>
      </div>
      {(githubUrl || linkedinUrl) && (
        <div className="mt-6 flex justify-center space-x-4 opacity-50 group-hover:opacity-100 transition-opacity duration-300">
          {githubUrl && (
            <a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-cyan-400 transition-colors">
              <Github size={24} />
            </a>
          )}
          {linkedinUrl && (
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-cyan-400 transition-colors">
              <Linkedin size={24} />
            </a>
          )}
        </div>
      )}
    </div>
  </GlassCard>
);

// Product card with new styling
const ProductCard = ({ icon, title, description, url }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className="block h-full">
    <GlassCard className="p-8 h-full group text-center md:text-left">
      <div className="flex flex-col md:flex-row items-center">
        <div className="flex-shrink-0 flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 text-white mb-6 md:mb-0 md:mr-6 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-cyan-500/50">
          {icon}
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <p className="mt-2 text-slate-400">{description}</p>
        </div>
      </div>
    </GlassCard>
  </a>
);

// --- Main "About Us" Component ---

export default function AboutUsPage() {
  const founders = [
    {
      name: 'Vinit Jain',
      title: 'Founder & Full Stack GenAI Developer',
      imageUrl: 'https://vinit-jains-bucket.s3.ap-south-1.amazonaws.com/profile-pic1.png',
      bio: 'The visionary architect of our core technology. A proud SIH 2023 winner, Vinit translates complex ideas into powerful, scalable AI-driven solutions.',
      githubUrl: 'https://github.com/dashingvinit',
      linkedinUrl: 'https://www.linkedin.com/in/vinit-p-jain-327444178/',
    },
    {
      name: 'Bhomit Jain',
      title: 'Product Manager',
      imageUrl: 'https://placehold.co/200x200/8B5CF6/FFFFFF?text=BJ',
      bio: 'The strategic mind behind our product roadmap. Bhomit ensures our solutions are innovative and perfectly aligned with user needs and market evolution.',
      linkedinUrl: '#',
    },
    {
      name: 'Dr. Evelyn Reed',
      title: 'Chief AI Officer',
      imageUrl: 'https://placehold.co/200x200/EC4899/FFFFFF?text=ER',
      bio: 'With a Ph.D. in Machine Learning, Dr. Reed leads our charge into the future of AI. She architects our sophisticated models and ethical AI frameworks.',
      linkedinUrl: '#',
    },
    {
      name: 'David Chen',
      title: 'Head of Growth',
      imageUrl: 'https://placehold.co/200x200/10B981/FFFFFF?text=DC',
      bio: "The engine behind our expansion. David's data-driven strategies connect our innovative products with a global audience, fueling our growth.",
      linkedinUrl: '#',
    },
  ];

  return (
    <div className="bg-slate-900 min-h-screen font-sans text-slate-300 relative">
      {/* Background Gradient Grid */}
      <div className="absolute inset-0 z-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(#22D3EE_1px,transparent_1px)] [background-size:32px_32px]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,#818CF8,transparent_40%)]"></div>
      </div>

      <main className="relative z-10">
        <div className="container mx-auto px-4">
          <PageHeader />

          <Section title="Our Origin Story" id="story">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center">
              <div className="text-lg leading-relaxed space-y-6 text-slate-400 text-center md:text-left">
                <p>
                  Our journey began not in a sprawling corporate campus, but from the dynamic world of a passionate
                  student entrepreneur who saw the potential for technology to be more intuitive, powerful, and
                  accessible.
                </p>
                <p>
                  This belief is the cornerstone of our ventures. We started with a simple, yet profound idea:
                  technology should be a tool of empowerment. This has driven us to build platforms that are not just
                  functional, but transformative.
                </p>
              </div>
              <div className="flex justify-center">
                <Bot size={128} className="text-cyan-400 opacity-30" />
              </div>
            </div>
          </Section>

          <Section title="Meet The Architects" id="team">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {founders.map((founder) => (
                <FounderCard key={founder.name} {...founder} />
              ))}
            </div>
          </Section>

          <Section title="Our Core Philosophy" id="philosophy">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center">
              <div className="flex justify-center order-last md:order-first">
                <BrainCircuit size={128} className="text-indigo-400 opacity-30" />
              </div>
              <div className="text-lg leading-relaxed space-y-6 text-slate-400 text-center md:text-left">
                <p>
                  We believe in a collaborative and agile approach. The name "Brogrammers" reflects our ethos – a
                  friendly, approachable, and highly skilled team that works together to build amazing things. We cut
                  through the jargon and focus on what truly matters: delivering exceptional products that exceed
                  expectations.
                </p>
                <p>
                  Our mission is to empower businesses and individuals with the tools they need to succeed in a
                  digital-first world. We are constantly exploring new technologies and methodologies to ensure our
                  partners and clients stay ahead of the curve.
                </p>
              </div>
            </div>
          </Section>

          <Section title="Our Innovation Hub" id="ventures">
            <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
              <ProductCard
                icon={<Code size={32} />}
                title="Brogrammers.in"
                description="A hub for cutting-edge web and mobile application development. We transform complex problems into elegant, user-centric solutions."
                url="http://brogrammers.in"
              />
              <ProductCard
                icon={<BrainCircuit size={32} />}
                title="Edunexora.com"
                description="An AI-powered platform designed to revolutionize learning. We create tools that make education more engaging, personalized, and effective."
                url="http://edunexora.com"
              />
            </div>
          </Section>

          <Section title="Shape the Future With Us" id="contact">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                We are always on the lookout for new challenges, collaborations, and opportunities to make a difference.
                Whether you have a groundbreaking idea, need expert development services, or share our passion for
                leveraging technology for good, we want to hear from you.
              </p>
              <button
                className="bg-gradient-to-r from-indigo-500 to-cyan-500 text-white font-bold py-4 px-10 rounded-full hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-cyan-500/50"
                onClick={() => (window.location.href = 'mailto:zenshkoolai@gmail.com')}>
                Let's Build Together
              </button>
            </div>
          </Section>
        </div>
      </main>
    </div>
  );
}
